import React, { useState, useEffect, useCallback } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Typography, 
    Statistic, 
    Spin, 
    DatePicker, 
    Space,
    Button,
    Table,
    Tag
} from 'antd';
import { 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { 
    BarChartOutlined, 
    RiseOutlined, 
    SyncOutlined,
    UserOutlined,
    DollarOutlined,
    TransactionOutlined
} from '@ant-design/icons';
import { useAdmin } from '../../hooks/useAdmin';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const GlobalStats = () => {
    const { getDashboard, getTradingStats, getHistoricalStats, loading } = useAdmin();
    const [data, setData] = useState(null);
    const [tradingData, setTradingData] = useState({ spot: [], futures: [] });

    const load = useCallback(async () => {
        const [dashRes, tradeRes, growthRes] = await Promise.all([
            getDashboard(),
            getTradingStats(),
            getHistoricalStats()
        ]);
        
        if (dashRes.success) setData(dashRes.data);
        if (tradeRes && tradeRes.success) setTradingData(tradeRes.data);
        if (growthRes && growthRes.success) setGrowthStats(growthRes.data);
    }, [getDashboard, getTradingStats, getHistoricalStats]);

    const [growthStats, setGrowthStats] = useState(null);

    useEffect(() => {
        load();
    }, [load]);

    if (loading && !data) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Spin size="large" tip="Analyse des données globales..." />
            </div>
        );
    }

    const stats = data?.stats || {};
    
    // Simulated data for graphs
    const growthData = growthStats?.volume?.map(v => ({
        month: new Date(v.date).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' }),
        volume: parseFloat(v.volume),
        users: growthStats.users.find(u => u.date === v.date)?.count || 0
    })) || [
        { month: 'Jan', users: 0, volume: 0 },
        { month: 'Feb', users: 0, volume: 0 },
        { month: 'Mar', users: 0, volume: 0 },
        { month: 'Apr', users: 0, volume: 0 },
        { month: 'May', users: 0, volume: 0 },
        { month: 'Jun', users: 0, volume: 0 },
    ];

    const distributionData = [
        { name: 'Spot', value: stats.total_spot_balance ? parseFloat(stats.total_spot_balance) : 0 },
        { name: 'Futures', value: stats.total_futures_balance ? parseFloat(stats.total_futures_balance) : 0 },
        { name: 'Investment', value: stats.total_investment_balance ? parseFloat(stats.total_investment_balance) : 0 },
    ];

    const totalFunds = distributionData.reduce((acc, curr) => acc + curr.value, 0);
    const distributionPercentages = distributionData.map(item => ({
        ...item,
        percentage: totalFunds > 0 ? ((item.value / totalFunds) * 100).toFixed(1) : 0
    }));

    return (
        <div className="admin-stats-inner">
            <div className="page-title-section" style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>
                        <BarChartOutlined style={{ marginRight: 12, color: '#6366f1' }} />
                        Statistiques Globales
                    </Title>
                    <Text type="secondary">Analyse approfondie des performances de la plateforme.</Text>
                </div>
                <Space>
                    <RangePicker />
                    <Button icon={<SyncOutlined />} type="primary">Exporter Rapport</Button>
                </Space>
            </div>

            {/* High Level KPI */}
            <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="glass-card">
                        <Statistic
                            title="Balance Spot (USDT)"
                            value={stats.total_spot_balance || 0}
                            precision={2}
                            valueStyle={{ color: '#6366f1' }}
                            prefix={<DollarOutlined />}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="success" strong>{stats.today_volume || 0} USDT </Text>
                            <Text type="secondary">volume 24h</Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="glass-card">
                        <Statistic
                            title="Utilisateurs"
                            value={stats.total_users || 0}
                            valueStyle={{ color: '#10b981' }}
                            prefix={<UserOutlined />}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="success" strong>+{stats.new_users_today || 0} </Text>
                            <Text type="secondary">aujourd'hui</Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="glass-card">
                        <Statistic
                            title="Investissements Actifs"
                            value={stats.active_investments || 0}
                            precision={2}
                            valueStyle={{ color: '#f59e0b' }}
                            prefix={<DollarOutlined />}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="warning" strong>+{stats.today_investments || 0} USDT </Text>
                            <Text type="secondary">aujourd'hui</Text>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="glass-card">
                        <Statistic
                            title="Transactions Spot 24h"
                            value={stats.today_trades || 0}
                            valueStyle={{ color: '#ef4444' }}
                            prefix={<TransactionOutlined />}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="danger" strong>{stats.pending_withdrawals_count || 0} </Text>
                            <Text type="secondary">retraits en attente</Text>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Charts Section */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={16}>
                    <Card title="Croissance & Volume" className="glass-card">
                        <div style={{ height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={growthData}>
                                    <defs>
                                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="month" stroke="#94a3b8" />
                                    <YAxis stroke="#94a3b8" />
                                    <Tooltip 
                                        contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                                        itemStyle={{ color: '#f1f5f9' }}
                                    />
                                    <Area type="monotone" dataKey="users" stroke="#6366f1" fillOpacity={1} fill="url(#colorUsers)" />
                                    <Area type="monotone" dataKey="volume" stroke="#10b981" fillOpacity={0} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title="Répartition des Revenus" className="glass-card">
                        <div style={{ height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={distributionData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {distributionData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16 }}>
                            {distributionPercentages.map((item, i) => (
                                <div key={i} style={{ textAlign: 'center' }}>
                                    <div style={{ width: 12, height: 12, background: COLORS[i], borderRadius: '50%', margin: '0 auto 8px' }}></div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{item.name}</Text>
                                    <br />
                                    <Text strong>{item.percentage}%</Text>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Trading Statistics Tables */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <Card title="Statistiques Trading Spot" className="glass-card">
                        <Table 
                            dataSource={tradingData.spot} 
                            rowKey="symbol"
                            pagination={false}
                            scroll={{ x: true }}
                            columns={[
                                { title: 'Paire', dataIndex: 'symbol', key: 'symbol', render: text => <Tag color="blue">{text}</Tag> },
                                { title: 'Ordres', dataIndex: 'total_orders', key: 'total_orders' },
                                { title: 'Volume (USDT)', dataIndex: 'total_volume_usdt', key: 'total_volume_usdt', render: val => val ? `$${parseFloat(val).toLocaleString()}` : '-' },
                                { title: 'Prix Moyen', dataIndex: 'avg_price', key: 'avg_price', render: val => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                            ]}
                            className="premium-table"
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="Statistiques Positions Futures" className="glass-card">
                        <Table 
                            dataSource={tradingData.futures} 
                            rowKey="symbol"
                            pagination={false}
                            scroll={{ x: true }}
                            columns={[
                                { title: 'Paire', dataIndex: 'symbol', key: 'symbol', render: text => <Tag color="purple">{text}</Tag> },
                                { title: 'Positions', dataIndex: 'total_positions', key: 'total_positions' },
                                { title: 'Volume (USDT)', dataIndex: 'total_volume_usdt', key: 'total_volume_usdt', render: val => val ? `$${parseFloat(val).toLocaleString()}` : '-' },
                                { title: 'Levier Moyen', dataIndex: 'avg_leverage', key: 'avg_leverage', render: val => val ? `${parseFloat(val).toFixed(1)}x` : '-' },
                                { title: 'Liq. Moyenne', dataIndex: 'avg_liquidation_price', key: 'avg_liquidation_price', render: val => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                { title: 'TP Moyen', dataIndex: 'avg_take_profit', key: 'avg_take_profit', render: val => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                { title: 'SL Moyen', dataIndex: 'avg_stop_loss', key: 'avg_stop_loss', render: val => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                            ]}
                            className="premium-table"
                        />
                    </Card>
                </Col>
            </Row>

            <style dangerouslySetInnerHTML={{ __html: `
                .glass-card {
                    background: rgba(30, 41, 59, 0.4) !important;
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255,255,255,0.05) !important;
                    border-radius: 16px !important;
                }
                .ant-statistic-title { color: #94a3b8 !important; font-size: 12px !important; font-weight: 600 !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; }
                .ant-statistic-content-value { font-weight: 800 !important; }
                .premium-table .ant-table { background: transparent !important; }
                .premium-table .ant-table-thead > tr > th { background: rgba(255,255,255,0.05) !important; color: #94a3b8 !important; border-bottom: 1px solid rgba(255,255,255,0.1) !important; }
                .premium-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(255,255,255,0.05) !important; color: #f1f5f9 !important; }
                .premium-table .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
                .premium-table .ant-empty-description { color: #94a3b8 !important; }
            `}} />
        </div>
    );
};

export default GlobalStats;
